/**
 * The remaining capabilities, as three cards under their own section head
 * (contracts/web-surface.md §2).
 *
 * ## Only what the app already does (FR-001)
 *
 * Every claim is a capability of the shipped iOS app: unlimited recording with
 * crash recovery (ac41), read-aloud (3533, premium since 61e9), and premium
 * note cleanup with its title and tag (61e9 — which retired the on-device
 * titles-and-tags card that used to sit here: the Foundation Models path went
 * dormant and free notes carry no generated title or tag). The "Later" section
 * further down is the page's one sanctioned exception to this rule; these
 * cards are not it.
 *
 * Two of the three are paid now and their copy says so plainly — "with
 * premium" is the scope, stated in the claim itself, not a badge. The cleanup
 * card must never imply the tidying happens on the phone (FR-028): the
 * "comes back" phrasing is deliberate, and the locality story lives in the
 * privacy section above.
 *
 * The bigger claims — privacy, self-linking, the second brain — each own a
 * full section above, so what is left here is what a reader would not guess
 * from those. Tone unchanged: plain sentences, outlined cards, no superlative.
 *
 * ## Headings (FR-019(e))
 *
 * Card titles are `<h3>` now: the redesign gave every section an `<h2>`, and
 * this section's is the SectionHead's "The rest of it."
 *
 * ## Layout
 *
 * A wrapping row of equal-width cards driven purely by available width — the
 * how-it-works pattern: `flexBasis` is the narrowest a card may be, `flexGrow`
 * spends the leftover evenly, and the row wraps by itself. Three across
 * inside the 1100 px column, two in the middle band, stacked when even one
 * card cannot hold its minimum. No `useWindowDimensions`, so a resize costs
 * no JS re-render (the old JS breakpoint stacked the ~700-768 px band that
 * now shows two-up — a deliberate change).
 */

import type { ComponentType } from "react";
import { StyleSheet, View } from "react-native";
import { Card, Text } from "react-native-paper";
import {
  MicrophoneGlyph,
  SparkleGlyph,
  SpeakerGlyph,
  type GlyphProps,
} from "@/components/icons";
import { MarketingSection } from "@/components/marketing/section";
import { SectionHead } from "@/components/marketing/section-head";
import { headingProps } from "@/lib/heading-props";
import { useAppTheme } from "@/theme/paper-theme";
import { iconSize, spacing } from "@/theme/tokens";

interface Feature {
  /** The card's `<h3>`, and the only place the claim is written down. */
  readonly heading: string;
  readonly body: string;
  /** Decorative — it restates the heading, so it is hidden from screen readers. */
  readonly Glyph: ComponentType<GlyphProps>;
}

const FEATURES: readonly Feature[] = [
  {
    heading: "Record for as long as it takes",
    body: "No cap on length. If a call cuts in or the app dies mid-sentence, the recording is still there when you come back.",
    Glyph: MicrophoneGlyph,
  },
  {
    heading: "Read back to you",
    body: "With premium, any note can be read aloud. Go back over a lecture on the walk home without looking at a screen.",
    Glyph: SpeakerGlyph,
  },
  {
    heading: "Your ramble, rewritten",
    body: "With premium, the three-minute version of a thought comes back as three clean sentences, named and tagged — the word-for-word transcript kept underneath, untouched.",
    Glyph: SparkleGlyph,
  },
];

const CARD_HEADING_PROPS = headingProps(3);

/**
 * The narrowest a card may be before the row wraps. `flexGrow` then spends the
 * leftover width evenly, so this sets the *count* per row rather than the size.
 */
const CARD_MIN_WIDTH = 320;

export function FeatureCards() {
  const theme = useAppTheme();

  return (
    <MarketingSection style={styles.section}>
      <SectionHead eyebrow="Also in the app" heading="The rest of it." />

      <View style={styles.grid}>
        {FEATURES.map(({ heading, body, Glyph }) => (
          <Card key={heading} mode="outlined" style={styles.card}>
            <Card.Content style={styles.cardContent}>
              <Glyph size={iconSize.xl} color={theme.colors.primary} />

              <Text
                variant="titleLarge"
                style={{ color: theme.colors.onSurface }}
                {...CARD_HEADING_PROPS}
              >
                {heading}
              </Text>

              <Text
                variant="bodyMedium"
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {body}
              </Text>
            </Card.Content>
          </Card>
        ))}
      </View>
    </MarketingSection>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: spacing.xl,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.lg,
  },
  card: {
    flexBasis: CARD_MIN_WIDTH,
    flexGrow: 1,
    // RN defaults flexShrink to 0: without this, a viewport narrower than
    // the basis (320px reflow floor minus the gutters) overflows instead of
    // letting the single-column card shrink to fit.
    flexShrink: 1,
  },
  cardContent: {
    gap: spacing.sm,
  },
});
